import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { memo, useEffect } from 'react';
import { useQuery } from 'react-query';

import { Dropdown } from 'shopper/components/Dropdown';
import Flex from 'shopper/components/Flex';

import { offerQueryKeys } from 'lib/queryKeys';
import { get } from 'lib/request';

import { useTopbar } from 'providers/TopbarProvider';

import ActiveCategoryGroupList from './ActiveCategoryGroupList';
import CategoryGroupNamesDropdownList from './CategoryGroupNamesDropdownList';
import SitemapGroupList from './SitemapGroupList';

import Z_INDEX_SCALE from 'constants/zIndexScale';

const Overlay = dynamic(() => import('shopper/components/Overlay'), {
  ssr: false,
});

const LAST_CATEGORY_SLUG_GROUPED_BY_SUBCATEGORIES = 'selecao/doubleeleven';

const CategoriesDropdown = ({
  activeCategory,
  isOpen,
  openerRef,
  setActiveCategory,
  onClose,
}) => {
  const { categories: initialData } = useTopbar();
  const theme = useTheme();
  const {
    data: categories,
    isFetched,
    isSuccess,
    refetch,
  } = useQuery(offerQueryKeys.categories({ includeOfferSelections: true }), {
    queryFn: () => get('/category?categories_selection=true'),
    initialData,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isOpen && !isFetched) {
      refetch();
    }
  }, [isFetched, isOpen, refetch]);

  const onCategoryClickHandler = (e) => {
    setActiveCategory(null);
    onClose(e);
  };

  if (!isSuccess) {
    return null;
  }

  return (
    <>
      {isOpen && <Overlay bg={theme.app.dropdown.overlay} full />}
      {isOpen && (
        <Dropdown
          id="categories-dropdown"
          data-test-selector="categories-dropdown"
          openerRef={openerRef}
          align="left"
          bg={theme.app.dropdown.innerBg}
          border="1px solid"
          borderColor={theme.app.dropdown.border}
          maxHeight="calc(100vh - 10rem)"
          open={isOpen}
          overflowY="auto"
          zIndex={Z_INDEX_SCALE.SIZE_6}
          onClickOutside={onClose}
          renderToHTML
        >
          <Flex
            alignItems="flex-start"
            position="relative"
            bg={theme.app.dropdown.innerBg}
            width={activeCategory ? '59rem' : '18.25rem'}
            top={0}
            left={0}
          >
            <Flex
              borderBottomRightRadius="0.25rem"
              borderRight="1px solid"
              borderBottom="0.75rem solid"
              borderRadius="4px"
              flexDirection="column"
              position="relative"
              top={0}
              left={0}
              borderColor={theme.app.dropdown.border}
            >
              <CategoryGroupNamesDropdownList
                activeCategory={activeCategory}
                categories={categories}
                setActiveCategory={setActiveCategory}
              />
            </Flex>
            {activeCategory && (
              <ActiveCategoryGroupList
                activeCategory={activeCategory}
                categories={categories}
                lastCategorySlugGroupedBySubcategories={
                  LAST_CATEGORY_SLUG_GROUPED_BY_SUBCATEGORIES
                }
                onCategoryClick={onCategoryClickHandler}
                onClose={onClose}
              />
            )}
          </Flex>
        </Dropdown>
      )}
      {/* This is used for SEO purposes... */}
      <SitemapGroupList
        display="none"
        categories={categories}
        lastCategorySlugGroupedBySubcategories={
          LAST_CATEGORY_SLUG_GROUPED_BY_SUBCATEGORIES
        }
      />
    </>
  );
};

CategoriesDropdown.defaultProps = {
  activeCategory: null,
};

CategoriesDropdown.propTypes = {
  activeCategory: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  openerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.instanceOf(
        typeof Element === 'undefined' ? () => {} : Element
      ),
    }),
  ]).isRequired,
  setActiveCategory: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default memo(CategoriesDropdown);
